import React from 'react';
import { Button, Dropdown, message } from 'antd';
import { DownloadOutlined, FileExcelOutlined, FilePdfOutlined, FileTextOutlined } from '@ant-design/icons';
import { exportToExcel, exportToCsv, exportToPdf } from '../utils/exportUtils';

/**
 * Reusable export button with Excel / CSV / PDF dropdown.
 *
 * Props:
 *   filename   {string}   base filename (no extension)
 *   sheetName  {string}   Excel sheet tab name (optional)
 *   title      {string}   report title for PDF header and Excel meta
 *   subtitle   {string}   optional subtitle line
 *   headers    {string[]} column header labels
 *   getData    {() => Array<Array<any>>}  function that returns rows at call time
 *   formats    {string[]} subset of ['excel','csv','pdf'] — default all three
 *   disabled   {boolean}
 *   size       {string}   ant button size
 *   style      {object}
 */
const ExportButton = ({
    filename = 'export',
    sheetName = 'Sheet1',
    title = 'Report',
    subtitle,
    headers = [],
    getData,
    formats = ['excel', 'csv', 'pdf'],
    disabled = false,
    size,
    style,
}) => {
    const handle = (format) => {
        const rows = getData?.() ?? [];
        if (!rows.length) {
            message.warning('No data to export');
            return;
        }

        const meta = { title, subtitle };

        if (format === 'excel') {
            exportToExcel(filename, sheetName, headers, rows, meta);
        } else if (format === 'csv') {
            exportToCsv(filename, headers, rows);
        } else if (format === 'pdf') {
            exportToPdf(filename, title, headers, rows, meta);
        }
    };

    const labelMap = {
        excel: { label: 'Export Excel (.xlsx)', icon: <FileExcelOutlined style={{ color: '#22c55e' }} /> },
        csv:   { label: 'Export CSV',            icon: <FileTextOutlined  style={{ color: '#3b82f6' }} /> },
        pdf:   { label: 'Export PDF',            icon: <FilePdfOutlined   style={{ color: '#ef4444' }} /> },
    };

    const items = formats.map(f => ({
        key: f,
        label: labelMap[f].label,
        icon: labelMap[f].icon,
        onClick: () => handle(f),
    }));

    return (
        <Dropdown menu={{ items }} disabled={disabled} trigger={['click']}>
            <Button icon={<DownloadOutlined />} size={size} style={style}>
                Export
            </Button>
        </Dropdown>
    );
};

export default ExportButton;
